import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import Chart from "react-apexcharts";
import { useGetGpuListQuery } from "../store/api/gpuApi";
import { aggregateGpuMetrics } from "../lib/gpuUtils";
import { Loader, ChartSkeletonLoader } from "../components/Loader";
import { TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

const CHART_COLORS = {
  committed: "#fca5a5",
  installed: "#c7d2fe",
  available: "#818cf8",
  allocated: "#4f46e5",
  utilised: "#3730a3",
};

function GpuDashboard(props) {
  return (
    <div className="">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">GPU Dashboard</h1>
        <p className="text-gray-600">
          Real-time GPU capacity monitoring and analytics
        </p>
      </div>
      <Tabs defaultValue="capacity">
        <TabsList className="mb-4">
          <TabsTrigger className="min-w-40" value="capacity">
            GPU Capacity
          </TabsTrigger>
          <TabsTrigger className="min-w-40" value="analytics">
            Analytics
          </TabsTrigger>
          <TabsTrigger className="min-w-40" value="gpu-services">
            GPU Services
          </TabsTrigger>
        </TabsList>
        <TabsContent value="capacity">
          <GpuCapacityTab />
        </TabsContent>
        <TabsContent value="analytics">
          <GpuAnalyticsTab />
        </TabsContent>
        <TabsContent value="gpu-services">
          <div className="text-center text-gray-500 py-12">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>GPU/ML Services dashboard coming soon...</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GpuCapacityTab() {
  const { data, isLoading } = useGetGpuListQuery(
    { limit: 100, offset: 0 },
    { pollingInterval: 0 },
  );

  if (isLoading)
    return (
      <div className="p-8">
        <Loader size="md" label="Loading GPU capacity data..." />
      </div>
    );

  if (!data?.data || data.data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No GPU data available</p>
      </div>
    );
  }

  const aggregated = aggregateGpuMetrics(data.data);

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Total Installed"
          value={aggregated.total_installed}
          icon="📦"
          color="bg-blue-50"
        />
        <KPICard
          title="Currently Allocated"
          value={aggregated.total_allocated}
          icon="🔧"
          color="bg-purple-50"
          trend={
            aggregated.total_allocated > aggregated.total_installed * 0.7
              ? "high"
              : "normal"
          }
        />
        <KPICard
          title="Currently Utilized"
          value={aggregated.total_utilised}
          icon="⚡"
          color="bg-green-50"
        />
        <KPICard
          title="Available"
          value={aggregated.total_available}
          icon="✅"
          color="bg-teal-50"
          trend={
            aggregated.total_available <= aggregated.total_installed * 0.2
              ? "low"
              : "normal"
          }
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <OverallGpuCapacity data={aggregated} />
        <GpuAllocationBreakdown data={aggregated} />
      </div>

      {/* Bar Chart */}
      <div className="mb-6">
        <GpuCapacityBarChart data={data.data} />
      </div>

      {/* Instance Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.data.map((gpu, index) => (
          <GpuCapacityUnits key={index} gpu={gpu} />
        ))}
      </div>
    </div>
  );
}

function GpuAnalyticsTab() {
  const { data, isLoading } = useGetGpuListQuery(
    { limit: 100, offset: 0 },
    { pollingInterval: 0 },
  );

  if (isLoading)
    return (
      <div className="p-8">
        <Loader size="md" label="Loading analytics data..." />
      </div>
    );

  if (!data?.data || data.data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No data for analytics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GpuUtilizationTrend data={data.data} />
      <GpuTypeDistribution data={data.data} />
    </div>
  );
}

function GpuCapacityBarChart({ data }) {
  const chartData = {
    options: {
      chart: {
        id: "gpu-capacity-bar",
        stacked: true,
        toolbar: { show: false },
        sparkline: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: "40%",
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 0 },
      xaxis: {
        categories: data.map((i) => i.gpuname || i.instance_type),
        labels: {
          rotate: -45,
          style: { fontSize: "10px", fontWeight: 600 },
        },
        axisTicks: { show: false },
      },
      yaxis: { labels: { show: true } },
      legend: { show: true, position: "top" },
    },
    series: [
      {
        name: "Utilised",
        color: CHART_COLORS.utilised,
        data: data.map((c) => c.gpu_utilised),
      },
      {
        name: "Allocated",
        color: CHART_COLORS.allocated,
        data: data.map((c) => c.gpu_allocated),
      },
      {
        name: "Available",
        color: CHART_COLORS.available,
        data: data.map((c) => c.gpu_available),
      },
    ],
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GPU Capacity by Instance</CardTitle>
        <CardDescription>
          Utilisation, Allocation, and Availability
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height={350}
        />
      </CardContent>
    </Card>
  );
}

function OverallGpuCapacity({ data }) {
  const chartData = [
    {
      label: "Available",
      value: data.total_available,
      fill: CHART_COLORS.available,
    },
    {
      label: "Allocated",
      value: data.total_allocated,
      fill: CHART_COLORS.allocated,
    },
    {
      label: "Utilised",
      value: data.total_utilised,
      fill: CHART_COLORS.utilised,
    },
  ];

  const chartConfig = {
    options: {
      chart: {
        type: "donut",
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      labels: chartData.map((i) => i.label),
      colors: chartData.map((i) => i.fill),
      dataLabels: {
        dropShadow: {
          enabled: true,
          color: "#000000",
          opacity: 0.2,
        },
        style: {
          fontSize: "12px",
          fontWeight: 500,
          colors: ["#fff"],
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: { show: true, offsetY: -6 },
              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                color: "#111",
              },
              total: {
                show: true,
                label: "Total GPUs",
                fontSize: "12px",
                fontWeight: 600,
                color: "#111",
                formatter: () => data.total_installed,
              },
            },
          },
        },
      },
    },
    series: chartData.map((i) => Number(i.value)),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GPU Capacity Donut</CardTitle>
        <CardDescription>Usage distribution</CardDescription>
      </CardHeader>
      <CardContent>
        <Chart
          options={chartConfig.options}
          series={chartConfig.series}
          type="donut"
          height={300}
        />
      </CardContent>
    </Card>
  );
}

function GpuAllocationBreakdown({ data }) {
  const totalUtilization =
    (data.total_utilised / data.total_installed) * 100 || 0;
  const totalAllocation =
    (data.total_allocated / data.total_installed) * 100 || 0;

  const chartData = {
    options: {
      chart: { type: "radialBar" },
      plotOptions: {
        radialBar: {
          dataLabels: {
            name: { fontSize: "22px" },
            value: { fontSize: "16px" },
            total: {
              show: true,
              label: "Allocation %",
              fontSize: "16px",
            },
          },
        },
      },
      colors: [CHART_COLORS.allocated, CHART_COLORS.utilised],
      labels: ["Allocated %", "Utilised %"],
    },
    series: [Math.min(totalAllocation, 100), Math.min(totalUtilization, 100)],
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GPU Utilization Rate</CardTitle>
        <CardDescription>Percentage of total capacity</CardDescription>
      </CardHeader>
      <CardContent>
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="radialBar"
          height={300}
        />
      </CardContent>
    </Card>
  );
}

function KPICard({ title, value, icon, color, trend }) {
  return (
    <Card className={`${color} border-0`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-2xl font-bold">{value}</div>
            {trend === "high" && (
              <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                <TrendingUp className="h-3 w-3" />
                High usage
              </div>
            )}
            {trend === "low" && (
              <div className="flex items-center gap-1 mt-1 text-orange-600 text-xs">
                <TrendingDown className="h-3 w-3" />
                Low availability
              </div>
            )}
          </div>
          <div className="text-4xl opacity-20">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function GpuCapacityUnits({ gpu }) {
  const utilizationPercent = (gpu.gpu_utilised / gpu.gpu_installed) * 100 || 0;
  const allocationPercent = (gpu.gpu_allocated / gpu.gpu_installed) * 100 || 0;

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm truncate">{gpu.gpuname}</CardTitle>
        <CardDescription className="text-xs">
          {gpu.instance_type} • {gpu.gpu_type}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bars */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">Utilisation</span>
            <span className="font-medium">
              {utilizationPercent.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full"
              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
            ></div>
          </div>
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">Allocation</span>
            <span className="font-medium">{allocationPercent.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full"
              style={{ width: `${Math.min(allocationPercent, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t">
          <div className="text-center">
            <div className="text-xs text-gray-500">Installed</div>
            <div className="text-lg font-bold">{gpu.gpu_installed}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Available</div>
            <div
              className={`text-lg font-bold ${
                gpu.gpu_available > 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {gpu.gpu_available}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GpuUtilizationTrend({ data }) {
  // Sort by instance for trend analysis
  const sortedData = [...data].sort(
    (a, b) =>
      (b.gpu_utilised / b.gpu_installed || 0) -
      (a.gpu_utilised / a.gpu_installed || 0),
  );

  const chartData = {
    options: {
      chart: { type: "area", sparkline: { enabled: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
        },
      },
      xaxis: { categories: sortedData.map((d) => d.gpuname) },
      yaxis: { title: { text: "Utilization %" } },
      colors: [CHART_COLORS.utilised],
    },
    series: [
      {
        name: "Utilisation %",
        data: sortedData.map(
          (d) => (d.gpu_utilised / d.gpu_installed) * 100 || 0,
        ),
      },
    ],
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GPU Utilization Trend</CardTitle>
        <CardDescription>Ranked by utilization rate</CardDescription>
      </CardHeader>
      <CardContent>
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="area"
          height={300}
        />
      </CardContent>
    </Card>
  );
}

function GpuTypeDistribution({ data }) {
  const typeCount = {};
  data.forEach((gpu) => {
    typeCount[gpu.gpu_type] = (typeCount[gpu.gpu_type] || 0) + 1;
  });

  const chartData = {
    options: {
      chart: { type: "pie" },
      labels: Object.keys(typeCount),
      colors: Object.keys(typeCount).map(
        (_, i) =>
          ["#4f46e5", "#6366f1", "#818cf8", "#a5b4fc", "#c7d2fe", "#e0c6fd"][
            i % 6
          ],
      ),
      legend: { position: "bottom" },
    },
    series: Object.values(typeCount),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GPU Type Distribution</CardTitle>
        <CardDescription>Number of GPUs by type</CardDescription>
      </CardHeader>
      <CardContent>
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="pie"
          height={300}
        />
      </CardContent>
    </Card>
  );
}

export default GpuDashboard;
